import React, { Suspense, ReactNode, useCallback } from "react";
import { Environment } from "../../../util/environment";
import { ErrorBoundary } from "./ErrorBoundary";
import MotifProgressLoader from "@ey/motif-react-wrapper/ProgressLoader";
import { useRegisteredComponent } from "@ey/voltron-core";
import config from "@ey/voltron-config";
import { FederatedObject } from "../types/FederatedObject";
import styled from "styled-components";

export interface RemoteComponentProps {
  federatedObject: FederatedObject;
  errorFallback?: ReactNode;
  runtimeErrorFallback?: ReactNode;
  [key: string]: any;
}

export function RemoteComponent({
  federatedObject,
  errorFallback = "Error loading component",
  runtimeErrorFallback = "An error occurred while rendering the component",
  ...props
}: RemoteComponentProps): JSX.Element {
  const { Component, errorLoading } = useRegisteredComponent(federatedObject);

  const boundaryId = `${federatedObject.scope}${federatedObject.module}`;

  const onRuntimeError = useCallback(
    (error: Error): void => {
      console.error(`Runtime error in component ${boundaryId}:`, error);
    },
    [boundaryId]
  );

  if (errorLoading) {
    return <StyledRemoteComponent className="error">{errorFallback}</StyledRemoteComponent>;
  }

  return (
    <StyledRemoteComponent>
      {Component ? (
        <ErrorBoundary id={boundaryId} fallback={errorFallback} onError={onRuntimeError}>
          <Suspense fallback={<MotifProgressLoader show variant="circular" />}>
            <Component
              loadingFallback={<MotifProgressLoader show variant="circular" />}
              federatedHostName={Environment.getFederatedHostName()}
              telemetry={false}
              runtimeErrorFallback={runtimeErrorFallback}
              onRuntimeError={onRuntimeError}
              adConfig={config.ad}
              {...props}
            />
          </Suspense>
        </ErrorBoundary>
      ) : (
        <MotifProgressLoader show variant="circular" />
      )}
    </StyledRemoteComponent>
  );
}

const StyledRemoteComponent = styled.section`
  display: flex;
  width: 100%;
  height: 100%;

  &.error {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .motif-progress-loader {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
  }
`;
